import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getBalloonResidualValuesThunk,
  getBalloonVehicleEnginesThunk,
  getBalloonVehicleInfoThunk,
  getLenderVehicleMaxMileageThunk,
} from 'actions/vehicleActions';
import { IRequestFlags } from 'modules/core/types';
import { IBalloonVehicle, IResidualValues } from 'modules/core/types/balloon';

type LoanVariablesDialogPayload<TPayload> = TPayload & { modalId: string };

export interface ILoanVariablesDialogState {
  currentModalId: string | null;
  isLoading: boolean;
  isFetchingFailed: boolean;
  loanVariables: any;
  loanTermsList: Array<number>;
  maxMileage: number;
  balloonVehicleInfo: IBalloonVehicle[];
  selectedBalloonVehicle: IBalloonVehicle | null;
  balloonVehicleEngines: string[];
  balloonResidualValues: IResidualValues | null;
  balloonVehicleInfoFlags: IRequestFlags;
  isGeneratingWorksheet: boolean;
  isPendingRecalculation: boolean;
}

export const initialState: ILoanVariablesDialogState = {
  currentModalId: null,
  isLoading: true,
  isFetchingFailed: false,
  loanVariables: {},
  loanTermsList: [],
  maxMileage: 0,
  balloonVehicleInfo: [],
  selectedBalloonVehicle: null,
  balloonVehicleEngines: [],
  balloonResidualValues: null,
  balloonVehicleInfoFlags: {
    isInvalid: false,
    isFetching: false,
    isLoaded: false,
  },
  isGeneratingWorksheet: false,
  isPendingRecalculation: false,
};

export const loanVariablesDialogSlice = createSlice({
  name: 'loanVariablesDialog',
  initialState,
  reducers: {
    resetComponentState: () => ({ ...initialState }),
    setCurrentModalId: (state: ILoanVariablesDialogState, { payload }: PayloadAction<string>) => {
      state.currentModalId = payload;
    },
    setIsLoading: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ isLoading: boolean }>>,
    ) => {
      const { modalId, isLoading } = payload;

      if (state.currentModalId === modalId) state.isLoading = isLoading;
    },
    setIsFetchingFailed: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ isFetchingFailed: boolean }>>,
    ) => {
      const { modalId, isFetchingFailed } = payload;

      if (state.currentModalId === modalId) state.isFetchingFailed = isFetchingFailed;
    },
    setLoanVariables: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ loanVariables: any }>>,
    ) => {
      const { modalId, loanVariables } = payload;

      if (state.currentModalId === modalId) state.loanVariables = loanVariables;
    },
    setLoanTermsList: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ loanTermsList: any }>>,
    ) => {
      const { modalId, loanTermsList } = payload;

      if (state.currentModalId === modalId) state.loanTermsList = loanTermsList;
    },
    setSelectedBalloonVehicle: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ selectedBalloonVehicle: IBalloonVehicle }>>,
    ) => {
      const { modalId, selectedBalloonVehicle } = payload;

      if (state.currentModalId === modalId) state.selectedBalloonVehicle = selectedBalloonVehicle;
    },
    resetBalloonState: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{}>>,
    ) => {
      const { modalId } = payload;

      if (state.currentModalId === modalId) {
        state.balloonVehicleInfo = initialState.balloonVehicleInfo;
        state.selectedBalloonVehicle = initialState.selectedBalloonVehicle;
        state.balloonVehicleEngines = initialState.balloonVehicleEngines;
        state.balloonResidualValues = initialState.balloonResidualValues;
        state.balloonVehicleInfoFlags = { ...initialState.balloonVehicleInfoFlags };
      }
    },
    setIsGeneratingWorksheet: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ isGeneratingWorksheet: boolean }>>,
    ) => {
      const { modalId, isGeneratingWorksheet } = payload;

      if (state.currentModalId === modalId) state.isGeneratingWorksheet = isGeneratingWorksheet;
    },
    setIsPendingRecalculation: (
      state: ILoanVariablesDialogState,
      { payload }: PayloadAction<LoanVariablesDialogPayload<{ isPendingRecalculation: boolean }>>,
    ) => {
      const { modalId, isPendingRecalculation } = payload;

      if (state.currentModalId === modalId) state.isPendingRecalculation = isPendingRecalculation;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBalloonVehicleInfoThunk.fulfilled, (state: ILoanVariablesDialogState, { payload, meta }) => {
      const modalId = meta?.arg?.modalId;

      if (modalId && state.currentModalId === modalId) {
        state.balloonVehicleInfo = payload;
        state.balloonVehicleInfoFlags = {
          isInvalid: false,
          isFetching: false,
          isLoaded: true,
        };
      }
    });
    builder.addCase(getBalloonVehicleInfoThunk.pending, (state: ILoanVariablesDialogState, { meta }) => {
      const modalId = meta?.arg?.modalId;

      if (modalId && state.currentModalId === modalId) {
        state.balloonVehicleInfoFlags = {
          isInvalid: false,
          isFetching: true,
          isLoaded: false,
        };
      }
    });
    builder.addCase(getBalloonVehicleInfoThunk.rejected, (state: ILoanVariablesDialogState, { meta }) => {
      const modalId = meta?.arg?.modalId;

      if (modalId && state.currentModalId === modalId) {
        state.balloonVehicleInfoFlags = {
          isInvalid: true,
          isFetching: false,
          isLoaded: true,
        };
      }
    });
    builder.addCase(
      getBalloonVehicleEnginesThunk.fulfilled,
      (state: ILoanVariablesDialogState, { payload, meta }: any) => {
        const modalId = meta?.arg?.modalId;

        if (modalId && state.currentModalId === modalId) state.balloonVehicleEngines = payload;
      },
    );
    builder.addCase(getBalloonResidualValuesThunk.fulfilled, (state: ILoanVariablesDialogState, { payload, meta }) => {
      const modalId = meta?.arg?.modalId;

      if (modalId && state.currentModalId === modalId) state.balloonResidualValues = payload;
    });
    builder.addCase(
      getLenderVehicleMaxMileageThunk.fulfilled,
      (state: ILoanVariablesDialogState, { payload, meta }) => {
        const modalId = meta?.arg?.modalId;

        if (modalId && state.currentModalId === modalId) state.maxMileage = payload;
      },
    );
  },
});

export const {
  resetComponentState,
  setCurrentModalId,
  setIsLoading,
  setIsFetchingFailed,
  setLoanVariables,
  setLoanTermsList,
  setSelectedBalloonVehicle,
  resetBalloonState,
  setIsGeneratingWorksheet,
  setIsPendingRecalculation,
} = loanVariablesDialogSlice.actions;
